import { useNavigate } from "react-router-dom";
import { X, ChevronRight, LogIn, PlayCircle } from "lucide-react";

/**
 * Modal shown when a guest taps any gated CTA. Three primary actions:
 *   1. Create account  (goes to /register)
 *   2. Log in          (goes to /login)
 *   3. Skip & take the demo (launches the interactive demo at /upload?demo=1)
 *
 * Triggered on every guest interaction (not throttled) — guests can dismiss
 * with the close X if they just want to keep browsing.
 */
export default function AuthGateModal({ open, onClose, message }) {
  const navigate = useNavigate();
  if (!open) return null;

  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  return (
    <div
      data-testid="auth-gate-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 250,
        background: "rgba(14, 34, 64, 0.82)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 22, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 380,
          background: "var(--surface)", borderRadius: 28,
          padding: "28px 22px 22px", position: "relative", textAlign: "center",
          boxShadow: "0 30px 60px -24px rgba(0,0,0,0.45)",
        }}
      >
        <button
          onClick={onClose}
          data-testid="auth-gate-close"
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            width: 34, height: 34, borderRadius: 17,
            background: "var(--surface-soft)", border: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={18} color="var(--text-secondary)" />
        </button>

        <h2 style={{
          color: "var(--navy)", fontSize: 26, fontWeight: 800,
          letterSpacing: -0.5, margin: "6px 0 12px",
        }}>
          Take a quick demo to unlock a sign-up bonus
        </h2>
        <p style={{
          color: "var(--text-secondary)", fontSize: 16, lineHeight: 1.5,
          margin: "0 0 22px", padding: "0 6px",
        }}>
          {message || "Upload a sample receipt and see how instant cashback works. Whatever cash you win lands in your wallet the moment you create an account."}
        </p>

        <button
          data-testid="auth-gate-take-tour"
          onClick={() => go("/upload?demo=1")}
          className="btn-primary"
          style={{
            width: "100%", display: "inline-flex", alignItems: "center",
            justifyContent: "center", gap: 8,
            padding: "14px 16px",
            fontSize: 13, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase",
          }}
        >
          <PlayCircle size={16} />
          Take the tour
          <ChevronRight size={16} />
        </button>

        <button
          data-testid="auth-gate-login"
          onClick={() => go("/login")}
          style={{
            width: "100%", marginTop: 10,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            background: "var(--surface-soft)",
            border: "1px solid var(--border-strong)",
            borderRadius: 999, padding: "13px 16px",
            color: "var(--text-primary)",
            fontSize: 13, fontWeight: 800, letterSpacing: 0.4,
            cursor: "pointer",
          }}
        >
          <LogIn size={16} />
          Sign in / Sign up
        </button>
      </div>
    </div>
  );
}
